<template>
  <figure class="quote-artwork">
    <img
      ref="image"
      :alt="backgroundAlt"
      :src="`${API}${artwork.url}`"
      :style="imgStyles"
      class="quote-artwork__image responsive-image"
      @load="handleImageLoad"
    />
    <div
      :style="overlayStyles"
      class="quote-artwork__overlay"
    >
      <p
        v-if="stateMessage"
        class="quote-artwork__state"
        role="status"
        aria-live="polite"
      >{{ stateMessage }}</p>
      <ol
        v-if="loadState === 'ready'"
        class="quote-artwork__quotes"
      >
        <li
          v-for="quote in displayQuotes"
          :key="quote.key"
          :aria-current="quote.isCurrent ? 'true' : null"
          :class="{
            'is-settled': quote.isSettled,
          }"
          :data-placement-mode="quote.placement ? quote.placement.mode : null"
          :style="quoteStyle(quote)"
          class="quote-artwork__quote"
        >{{ quote.text }}</li>
      </ol>
      <span
        v-if="measuringQuoteIndex !== null"
        ref="measurer"
        :style="measurerStyles"
        aria-hidden="true"
        class="quote-artwork__measurer"
      >{{ measurerText }}</span>
    </div>
  </figure>
</template>

<script>
const {
  advanceQuoteTimeline,
  approvedQuoteSignature,
  buildQuoteSeekState,
  formatArtworkQuote,
  normalizeApprovedQuotes,
  remainingQuotePhaseMs,
} = require('@/lib/quote-artwork');
const { placeRectangle } = require('@/lib/quote-placement');

const POLL_INTERVAL_MS = 30000;
const RESIZE_DEBOUNCE_MS = 100;
const MIN_FONT_PX = 14;
const MAX_FONT_PX = 48;
const REFERENCE_WIDTH_PER_FONT_PX = 40;
const MAX_TEXT_WIDTH_RATIO = 0.90;

export default {
  name: 'QuoteArtwork',
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    artwork: {
      type: Object,
      required: true,
    },
    imgStyles: {
      type: [Object, String],
      default: '',
    },
    projectId: {
      type: [Number, String],
      required: true,
    },
    paused: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      artworkBounds: { width: 0, height: 0 },
      approvedQuoteSignature: null,
      destroyed: false,
      fontSize: MIN_FONT_PX,
      imageReady: false,
      loadState: 'loading',
      lifecyclePhase: 'empty',
      maxTextWidth: 1,
      measurementGeneration: 0,
      measuringQuoteIndex: null,
      needsReflow: false,
      overlayBounds: {
        height: 0,
        left: 0,
        top: 0,
        width: 0,
      },
      placementQueue: [],
      placements: [],
      phaseDueAt: null,
      phaseRemainingMs: 0,
      playbackPaused: this.paused,
      pollTimer: null,
      quotes: [],
      requestGeneration: 0,
      resizeTimer: null,
      revealTimer: null,
      sequenceCompleteEmitted: false,
      settledCount: 0,
      visibleCount: 0,
    };
  },
  computed: {
    API() {
      return this.$API;
    },
    backgroundAlt() {
      return 'Project quote artwork background';
    },
    displayQuotes() {
      return this.quotes.slice(0, this.visibleCount).map((quote, index) => ({
        id: quote.id,
        isCurrent: index === this.visibleCount - 1 && index >= this.settledCount,
        isSettled: index < this.settledCount,
        key: quote.id,
        placement: this.placements[index],
        text: formatArtworkQuote(quote.quote),
      }));
    },
    hasArtworkBounds() {
      return this.artworkBounds.width > 0 && this.artworkBounds.height > 0;
    },
    measurerStyles() {
      return {
        fontSize: `${this.fontSize}px`,
        maxWidth: `${this.maxTextWidth}px`,
      };
    },
    measurerText() {
      const quote = this.quotes[this.measuringQuoteIndex];
      return quote ? formatArtworkQuote(quote.quote) : '';
    },
    overlayStyles() {
      return {
        height: `${this.overlayBounds.height}px`,
        left: `${this.overlayBounds.left}px`,
        top: `${this.overlayBounds.top}px`,
        width: `${this.overlayBounds.width}px`,
      };
    },
    sequenceSource() {
      return {
        artworkUrl: this.artwork && this.artwork.url,
        projectId: String(this.projectId),
      };
    },
    stateMessage() {
      if (this.loadState === 'loading') return 'Loading quotes…';
      if (this.loadState === 'empty') return 'No approved quotes yet.';
      if (this.loadState === 'error') return 'Quotes are temporarily unavailable.';
      return '';
    },
  },
  watch: {
    paused(isPaused) {
      if (!this.active || isPaused === this.playbackPaused) return;
      if (isPaused) this.pausePlayback();
      else this.playPlayback();
    },
    active(isActive) {
      if (!isActive) {
        this.suspendLifecycle(false);
      } else {
        this.activateSequence();
      }
    },
    sequenceSource(newSource, oldSource) {
      if (
        oldSource
        && newSource.projectId === oldSource.projectId
        && newSource.artworkUrl === oldSource.artworkUrl
      ) return;
      if (!oldSource || newSource.artworkUrl !== oldSource.artworkUrl) {
        this.imageReady = false;
      }
      this.resetSequence();
      this.loadApprovedQuotes(false);
    },
  },
  methods: {
    activateSequence() {
      this.playbackPaused = this.paused;
      this.$nextTick(() => {
        if (this.destroyed || !this.active || !this.updateArtworkBounds()) return;
        if (this.needsReflow) {
          this.reflowVisibleQuotes();
        } else if (this.visibleCount > 0 && !this.placements[this.visibleCount - 1]) {
          this.measureAndPlaceQuote(this.visibleCount - 1);
        }
        this.resumeLifecycleWhenReady();
        this.emitTimelineChange();
      });
    },
    clearPollTimer() {
      if (this.pollTimer !== null) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    clearResizeTimer() {
      if (this.resizeTimer !== null) {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = null;
      }
    },
    clearSequenceTimers() {
      if (this.revealTimer !== null) {
        clearTimeout(this.revealTimer);
        this.revealTimer = null;
      }
    },
    currentTimelineState() {
      return {
        completed: this.sequenceCompleteEmitted,
        phase: this.lifecyclePhase,
        position: this.visibleCount,
        remainingMs: this.phaseRemainingMs,
        settledCount: this.settledCount,
        total: this.quotes.length,
      };
    },
    applyTimelineState(state) {
      this.lifecyclePhase = state.phase;
      this.phaseRemainingMs = state.remainingMs;
      this.settledCount = state.settledCount;
      this.visibleCount = state.position;
      for (let index = 0; index < this.visibleCount; index += 1) {
        this.measureAndPlaceQuote(index);
      }
    },
    advanceLifecyclePhase() {
      this.phaseDueAt = null;
      this.phaseRemainingMs = 0;
      const nextState = advanceQuoteTimeline(this.currentTimelineState());
      this.applyTimelineState(nextState);
      if (nextState.completed) {
        this.completeSequence();
        return;
      }
      this.emitTimelineChange(true);
      this.scheduleLifecyclePhase();
    },
    scheduleLifecyclePhase() {
      this.clearSequenceTimers();
      if (
        this.destroyed
        || !this.active
        || this.playbackPaused
        || !this.imageReady
        || !this.hasArtworkBounds
        || this.loadState !== 'ready'
        || this.lifecyclePhase === 'empty'
        || this.lifecyclePhase === 'completed'
      ) return;
      this.phaseDueAt = Date.now() + this.phaseRemainingMs;
      if (this.lifecyclePhase === 'intro' || this.lifecyclePhase === 'white-hold') {
        this.revealTimer = setTimeout(this.advanceLifecyclePhase, this.phaseRemainingMs);
      }
    },
    suspendLifecycle(markPaused) {
      if (this.phaseDueAt !== null) {
        this.phaseRemainingMs = remainingQuotePhaseMs(this.phaseDueAt, Date.now());
      }
      this.phaseDueAt = null;
      this.clearSequenceTimers();
      if (markPaused) {
        this.playbackPaused = true;
        this.emitTimelineChange(false);
      }
    },
    pausePlayback() {
      this.suspendLifecycle(true);
    },
    playPlayback() {
      if (this.loadState !== 'loading' && (this.loadState !== 'ready' || this.quotes.length === 0)) {
        this.playbackPaused = true;
        this.emitTimelineChange(false);
        return;
      }
      if (this.sequenceCompleteEmitted || this.lifecyclePhase === 'completed') {
        this.sequenceCompleteEmitted = false;
        this.applyTimelineState(buildQuoteSeekState(this.quotes.length, 0));
        this.$emit('sequence-reset');
        this.emitTimelineChange(false);
      }
      this.playbackPaused = false;
      // Keep Play intent while the API or image is still loading. Audio remains
      // gesture-bound in Home; readiness only starts the visual clock.
      this.resumeLifecycleWhenReady();
      this.emitTimelineChange();
    },
    completeSequence() {
      if (this.sequenceCompleteEmitted) return;
      this.lifecyclePhase = 'completed';
      this.phaseDueAt = null;
      this.phaseRemainingMs = 0;
      this.playbackPaused = true;
      this.sequenceCompleteEmitted = true;
      this.emitTimelineChange(false);
      this.$emit('sequence-complete');
    },
    emitTimelineChange(
      playing = this.active && !this.playbackPaused && !this.sequenceCompleteEmitted,
    ) {
      this.$emit('timeline-change', {
        completed: this.sequenceCompleteEmitted,
        playing,
        position: this.visibleCount,
        total: this.quotes.length,
      });
    },
    handleImageLoad() {
      this.imageReady = true;
      this.$nextTick(() => {
        if (this.destroyed || !this.updateArtworkBounds()) return;
        this.resumeLifecycleWhenReady();
      });
    },

    handleResize() {
      this.clearResizeTimer();
      this.resizeTimer = setTimeout(() => {
        this.resizeTimer = null;
        if (this.destroyed) return;
        if (!this.active) {
          this.needsReflow = true;
          return;
        }
        this.reflowVisibleQuotes();
      }, RESIZE_DEBOUNCE_MS);
    },
    loadApprovedQuotes(emitResetOnReady = true, isPoll = false) {
      this.clearPollTimer();
      const generation = this.requestGeneration + 1;
      const { projectId } = this;
      this.requestGeneration = generation;
      this.axios
        .get(`${this.$API}/projects/${encodeURIComponent(projectId)}/approved-quotes`)
        .then((response) => {
          if (this.destroyed || generation !== this.requestGeneration) return;
          if (!response.data || !Array.isArray(response.data.quotes)) {
            if (!isPoll) {
              this.quotes = [];
              this.loadState = 'error';
              this.completeSequence();
            }
            return;
          }
          const quotes = normalizeApprovedQuotes(response.data);
          const signature = approvedQuoteSignature(quotes);
          if (isPoll && signature === this.approvedQuoteSignature) return;
          if (isPoll) {
            this.rebuildApprovedQuoteSequence(quotes, signature);
            return;
          }
          this.approvedQuoteSignature = signature;
          this.quotes = quotes;
          this.loadState = this.quotes.length ? 'ready' : 'empty';
          this.emitTimelineChange();
          if (this.quotes.length) {
            if (emitResetOnReady) this.$emit('sequence-reset');
          } else {
            this.completeSequence();
          }
          this.resumeLifecycleWhenReady();
        })
        .catch(() => {
          if (this.destroyed || generation !== this.requestGeneration) return;
          if (!isPoll) {
            this.quotes = [];
            this.loadState = 'error';
            this.completeSequence();
          }
        })
        .then(() => {
          if (this.destroyed || generation !== this.requestGeneration) return;
          this.scheduleApprovedQuotePoll();
        });
    },
    measureAndPlaceQuote(index) {
      if (
        !this.hasArtworkBounds
        || !this.quotes[index]
        || this.placements[index]
        || this.placementQueue.indexOf(index) !== -1
        || this.measuringQuoteIndex === index
      ) return;
      this.placementQueue.push(index);
      this.measureNextQuote();
    },
    measureNextQuote() {
      if (this.destroyed || this.measuringQuoteIndex !== null) return;
      const index = this.placementQueue.shift();
      if (index == null) return;
      if (!this.quotes[index] || this.placements[index]) {
        this.measureNextQuote();
        return;
      }
      const generation = this.measurementGeneration;
      this.measuringQuoteIndex = index;
      this.$nextTick(() => {
        if (this.destroyed || generation !== this.measurementGeneration) return;
        const { measurer } = this.$refs;
        if (!measurer) return;
        const measured = measurer.getBoundingClientRect();
        const placement = placeRectangle({
          bounds: this.artworkBounds,
          occupied: this.placements.filter(Boolean),
          size: {
            height: measured.height,
            width: measured.width,
          },
        });
        this.$set(this.placements, index, placement);
        this.measuringQuoteIndex = null;
        this.measureNextQuote();
      });
    },
    quoteStyle(quote) {
      const { placement } = quote;
      return {
        fontSize: `${this.fontSize}px`,
        left: placement ? `${placement.x}px` : '0',
        maxWidth: `${this.maxTextWidth}px`,
        top: placement ? `${placement.y}px` : '0',
        visibility: placement ? 'visible' : 'hidden',
        width: placement ? `${placement.width}px` : 'auto',
      };
    },
    seekQuotePosition(position) {
      const state = buildQuoteSeekState(this.quotes.length, position);
      this.clearSequenceTimers();
      this.phaseDueAt = null;
      this.playbackPaused = true;
      this.sequenceCompleteEmitted = false;
      this.applyTimelineState(state);
      this.$emit('sequence-reset');
      this.emitTimelineChange(false);
    },
    rebuildApprovedQuoteSequence(quotes, signature) {
      const wasPlaying = !this.playbackPaused && !this.sequenceCompleteEmitted;
      const wasIntro = this.lifecyclePhase === 'intro';
      this.suspendLifecycle(false);
      const introRemainingMs = this.phaseRemainingMs;
      this.measurementGeneration += 1;
      this.measuringQuoteIndex = null;
      this.needsReflow = false;
      this.placementQueue = [];
      this.placements = [];
      this.approvedQuoteSignature = signature;
      this.quotes = quotes;
      this.loadState = quotes.length ? 'ready' : 'empty';
      this.lifecyclePhase = wasIntro && quotes.length ? 'intro' : 'empty';
      this.phaseDueAt = null;
      this.phaseRemainingMs = wasIntro && quotes.length ? introRemainingMs : 0;
      this.playbackPaused = !wasPlaying;
      this.sequenceCompleteEmitted = false;
      this.settledCount = 0;
      this.visibleCount = 0;
      this.$emit('sequence-reset');
      this.emitTimelineChange();
      if (quotes.length && wasPlaying) {
        this.$nextTick(this.resumeLifecycleWhenReady);
      } else if (!quotes.length) this.completeSequence();
    },
    reflowVisibleQuotes() {
      if (this.destroyed || !this.active || !this.updateArtworkBounds()) {
        this.needsReflow = true;
        return;
      }
      this.measurementGeneration += 1;
      this.measuringQuoteIndex = null;
      this.needsReflow = false;
      this.placementQueue = [];
      this.placements = [];
      for (let index = 0; index < this.visibleCount; index += 1) {
        this.measureAndPlaceQuote(index);
      }
    },
    resetSequence() {
      this.clearPollTimer();
      this.clearSequenceTimers();
      this.measurementGeneration += 1;
      this.requestGeneration += 1;
      this.loadState = 'loading';
      this.lifecyclePhase = 'empty';
      this.measuringQuoteIndex = null;
      this.needsReflow = false;
      this.placementQueue = [];
      this.placements = [];
      this.phaseDueAt = null;
      this.phaseRemainingMs = 0;
      this.playbackPaused = this.paused;
      this.quotes = [];
      this.approvedQuoteSignature = null;
      this.sequenceCompleteEmitted = false;
      this.settledCount = 0;
      this.visibleCount = 0;
      this.$emit('sequence-reset');
      this.emitTimelineChange();
    },
    scheduleApprovedQuotePoll() {
      this.clearPollTimer();
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null;
        if (this.destroyed) return;
        this.loadApprovedQuotes(true, true);
      }, POLL_INTERVAL_MS);
    },
    resumeLifecycleWhenReady() {
      if (
        !this.destroyed
        && this.active
        && this.imageReady
        && this.hasArtworkBounds
        && this.loadState === 'ready'
        && this.quotes.length > 0
        && !this.playbackPaused
        && !this.sequenceCompleteEmitted
      ) {
        if (this.lifecyclePhase === 'empty') {
          this.applyTimelineState(buildQuoteSeekState(this.quotes.length, 0));
        }
        // Image load, activation and API readiness can arrive in any order.
        // Never rewind a clock that is already running.
        if (this.phaseDueAt === null) this.scheduleLifecyclePhase();
        this.emitTimelineChange();
      }
    },
    updateArtworkBounds() {
      const { image } = this.$refs;
      if (!image) return false;
      const rectangle = image.getBoundingClientRect();
      if (!(rectangle.width > 0 && rectangle.height > 0)) return false;
      this.artworkBounds = {
        height: rectangle.height,
        width: rectangle.width,
      };
      this.overlayBounds = {
        height: rectangle.height,
        left: rectangle.left,
        top: rectangle.top,
        width: rectangle.width,
      };
      this.fontSize = Math.max(
        MIN_FONT_PX,
        Math.min(
          MAX_FONT_PX,
          Math.round(rectangle.width / REFERENCE_WIDTH_PER_FONT_PX),
        ),
      );
      this.maxTextWidth = Math.max(1, Math.round(rectangle.width * MAX_TEXT_WIDTH_RATIO));
      return true;
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.loadApprovedQuotes();
  },
  beforeDestroy() {
    this.destroyed = true;
    this.measurementGeneration += 1;
    this.requestGeneration += 1;
    this.clearPollTimer();
    this.clearResizeTimer();
    this.clearSequenceTimers();
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
.quote-artwork {
  height: 100%;
  margin: 0;
  pointer-events: none;
  width: 100%;
}

.quote-artwork__image {
  bottom: 0;
  left: 0;
  margin: auto;
  max-height: calc(100% - 80px);
  max-width: 100%;
  object-fit: contain;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 0;
}

.quote-artwork__overlay {
  overflow: hidden;
  pointer-events: none;
  position: fixed;
  z-index: 1;
}

.quote-artwork__state {
  color: #fff;
  margin: 20px;
}

.quote-artwork__quotes {
  list-style: none;
  margin: 0;
  padding: 0;
}

.quote-artwork__quote,
.quote-artwork__measurer {
  color: #fff;
  font-family: Helvetica, "Helvetica Neue", Arial, sans-serif;
  font-weight: 700;
  line-height: 1;
  overflow-wrap: break-word;
  text-align: left;
}

.quote-artwork__quote {
  margin: 0;
  position: absolute;
}

.quote-artwork__measurer {
  display: inline-block;
  left: 0;
  position: absolute;
  top: 0;
  visibility: hidden;
}

.quote-artwork__quote.is-settled {
  color: #000;
}

</style>
